/* src/AuthGuard.js */

// --- React Imports ---
import React, { useEffect, useState } from 'react';
import { Navigate, useLocation, Outlet } from 'react-router-dom';

// --- Context Imports ---
import { useProfile } from './contexts/ProfileContext';
import { useLoading as useLoadingContextHook } from './contexts/LoadingContext';

// --- UI Imports ---
import LoadingScreen from './components/ui/LoadingScreen'; // Or your loading component

const AuthGuard = () => {
    const location = useLocation();
    const {
        authUser,
        isLoading,
        userSubscriptionStatus,
        userSubscriptionEndDate,
        activeProfileId,
    } = useProfile();
    const { showLoading, hideLoading } = useLoadingContextHook();

    useEffect(() => {
        if (isLoading) {
            showLoading("Verifying access...");
        } else {
            hideLoading();
        }
        return () => hideLoading();
    }, [isLoading, showLoading, hideLoading]);


    // --- Loading State ---
    // This is still important for guarding direct access to a protected route on page refresh.
    if (isLoading) {
        console.log(`[AuthGuard] Loading... Path: ${location.pathname}`);
        return <LoadingScreen message="Verifying access..." />;
    }

    // --- Authentication Check ---
    if (!authUser) {
        console.log(`[AuthGuard] No user authenticated. Redirecting to login from ${location.pathname}`);
        return <Navigate to="/login" state={{ from: location }} replace />;
    }
    
    // --- The Active Profile Check is NO LONGER NEEDED HERE ---
    // The new /loading route handles this logic correctly before we ever get here.
    // If the code reaches this point, we can assume a valid profile has been selected.

    // --- Subscription Check (can run immediately after auth check) ---
    const isActive = userSubscriptionStatus === 'active';
    const isTrialing = userSubscriptionStatus === 'trialing';
    const isCanceledWithAccess = userSubscriptionStatus === 'canceled' &&
                               userSubscriptionEndDate &&
                               new Date(userSubscriptionEndDate) >= new Date();

    const hasValidSubscription = isActive || isTrialing || isCanceledWithAccess;

    // The '/profile-selection' page itself is protected by this guard, so we must allow access to it.
    // The same applies to other auth-only paths.
    const authOnlyPaths = [
        '/account',
        '/settings',
        '/profile-selection',
        '/choose-plan',
    ];
    
    if (!hasValidSubscription && !authOnlyPaths.includes(location.pathname)) {
        console.log(`[AuthGuard] Subscription not active for ${location.pathname}. Status: ${userSubscriptionStatus}. EndDate: ${userSubscriptionEndDate}. Redirecting to /resubscribe.`);
        return <Navigate to="/resubscribe" state={{ from: location }} replace />;
    }

    // --- Access Granted ---
    console.log(`[AuthGuard] Access Granted to ${location.pathname}. Auth OK, Subscription: ${userSubscriptionStatus}.`);
    return <Outlet />;
};

export default AuthGuard;